import { ButtonLink } from '@mela/design-system';
import { SectionHead } from '../components/SectionHead';

interface FeatureCardProps {
  pill: string;
  pillVariant?: 'saffron' | 'violet';
  heading: React.ReactNode;
  paragraph: string;
  features: string[];
  cta: { label: string; href: string; variant: 'primary' | 'violet' };
}

const exhibitor: FeatureCardProps = {
  pill: 'For exhibitors',
  pillVariant: 'saffron',
  heading: (
    <>
      Capture every lead.<br />Follow up at scale.
    </>
  ),
  paragraph:
    'The booth pilot for serious exhibitors. Multi-modal capture, AI lead scoring, follow-ups in your buyer’s language, live ROI dashboard.',
  features: [
    'Capture in 3 seconds — voice, tap, photo, or manual',
    'AI scores leads Hot / Warm / Cold by hour 2 of the event',
    'WhatsApp follow-ups drafted in Gujarati, Hindi, Tamil — reviewed by you',
    'Works offline · Syncs when connection returns',
    'Live team dashboard — every booth staff in real time',
  ],
  cta: { label: 'Start at ₹999 per event →', href: '/pricing', variant: 'primary' },
};

const visitor: FeatureCardProps = {
  pill: 'For visitors',
  pillVariant: 'violet',
  heading: (
    <>
      Walk 5 hours.<br />Remember every stall.
    </>
  ),
  paragraph:
    'One tap saves a stall. End of event, you get a personalized microsite — every booth, what they offered, your voice notes, AI summaries — delivered to WhatsApp.',
  features: [
    'One tap to save · NFC, QR, or manual',
    'Pre-event matchmaking — who to meet before you arrive',
    'Live floor map · Push notifications when you’re near a saved booth',
    'Cross-event memory — your interests follow you',
    'Free forever · ₹199/year for unlimited contacts',
  ],
  cta: { label: 'Get your Mela ID — free →', href: '#waitlist', variant: 'violet' },
};

export function TwoCol() {
  return (
    <section className="px-6 sm:px-8 pt-24 sm:pt-32 pb-8" id="organizers">
      <div className="container-mela">
        <SectionHead
          eyebrow="Two sides, one platform"
          title={
            <>
              Built for both ends of every <span className="text-saffron-500">handshake</span>.
            </>
          }
          lede="Most event tools serve organizers and ignore everyone else. Mela starts with the people doing the actual business — and lets organizers run on rails that already work."
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FeatureCard {...exhibitor} />
          <FeatureCard {...visitor} />
        </div>
      </div>
    </section>
  );
}

function FeatureCard({ pill, pillVariant = 'saffron', heading, paragraph, features, cta }: FeatureCardProps) {
  const pillClass =
    pillVariant === 'saffron'
      ? 'bg-saffron-50 text-saffron-700 border-saffron-200'
      : 'bg-violet-50 text-violet-700 border-violet-200';

  return (
    <article className="bg-white border border-ink-200 rounded-2xl p-8 sm:p-10 transition-[transform,box-shadow,border-color] duration-base ease-mela hover:-translate-y-0.5 hover:shadow-elevated hover:border-ink-300">
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 border rounded-full font-mono text-[11px] font-medium tracking-wide mb-5 ${pillClass}`}
      >
        {pill}
      </span>

      <h3 className="font-display text-[26px] sm:text-[32px] leading-[1.05] -tracking-[0.025em] font-extrabold text-ink-900 mb-3">
        {heading}
      </h3>

      <p className="text-[15px] leading-snug text-ink-600 mb-6">{paragraph}</p>

      <ul className="mb-6">
        {features.map((f, i) => (
          <li
            key={i}
            className={`py-2.5 flex items-start gap-2.5 text-[14px] text-ink-700 leading-snug ${
              i === features.length - 1 ? '' : 'border-b border-ink-200'
            }`}
          >
            <span className="w-[18px] h-[18px] rounded-full bg-green-50 text-green-700 flex items-center justify-center flex-shrink-0 mt-0.5 text-[11px] font-bold">
              ✓
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <ButtonLink href={cta.href} variant={cta.variant} size="lg">
        {cta.label}
      </ButtonLink>
    </article>
  );
}
